import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  Package, Plus, Edit, Trash2, Check, X, DollarSign,
  Building2, Users, Layers, Settings, History, Copy,
  Eye, EyeOff, Star, AlertCircle, ChevronRight, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PlansManagementPage = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [showPricingDialog, setShowPricingDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [modulesCatalog, setModulesCatalog] = useState([]);
  const [limitsCatalog, setLimitsCatalog] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state
  const [planForm, setPlanForm] = useState({
    nombre: '',
    codigo: '',
    descripcion_breve: '',
    descripcion_comercial: '',
    publico_objetivo: '',
    color: '#3B82F6',
    orden: 0,
    etiqueta_destacada: '',
    texto_boton: 'Comenzar',
    es_recomendado: false,
    requiere_cotizacion: false,
    contratacion_directa: true,
    features: []
  });

  const [pricingForm, setPricingForm] = useState({
    precio_mensual: 0,
    precio_anual: 0,
    moneda: 'CLP',
    descuento_anual_porcentaje: 20,
    periodo_prueba_dias: 14,
    cobro_usuario_adicional: 0,
    cobro_establecimiento_adicional: 0
  });

  const [newFeature, setNewFeature] = useState('');

  // Auth check
  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || user.role !== 'super_admin') {
      toast.error('Acceso denegado. Solo superadministradores.');
      navigate('/app/dashboard');
      return;
    }
    
    fetchPlans();
    fetchCatalogs();
  }, []);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json'
  });

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${API_URL}/api/plans?include_archived=true`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPlans(data);
      } else {
        toast.error('Error al cargar planes');
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const fetchCatalogs = async () => {
    try {
      const [modulesRes, limitsRes] = await Promise.all([
        fetch(`${API_URL}/api/plans/modules/catalog`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/plans/limits/catalog`, { headers: getAuthHeaders() })
      ]);
      
      if (modulesRes.ok) {
        const data = await modulesRes.json();
        setModulesCatalog(data.modules || []);
      }
      if (limitsRes.ok) {
        const data = await limitsRes.json();
        setLimitsCatalog(data.limits || []);
      }
    } catch (error) {
      console.error('Error fetching catalogs:', error);
    }
  };

  const handleCreatePlan = async () => {
    if (!planForm.nombre || !planForm.codigo) {
      toast.error('Nombre y código son obligatorios');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/plans`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...planForm,
          pricing: pricingForm
        })
      });

      if (response.ok) {
        toast.success('Plan creado correctamente');
        setShowPlanDialog(false);
        resetForm();
        fetchPlans();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al crear plan');
      }
    } catch (error) {
      console.error('Error creating plan:', error);
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePlan = async () => {
    if (!selectedPlan) return;

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/plans/${selectedPlan.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(planForm)
      });

      if (response.ok) {
        toast.success('Plan actualizado correctamente');
        setShowPlanDialog(false);
        resetForm();
        fetchPlans();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al actualizar plan');
      }
    } catch (error) {
      console.error('Error updating plan:', error);
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePricing = async () => {
    if (!selectedPlan) return;

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/plans/${selectedPlan.id}/pricing`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(pricingForm)
      });

      if (response.ok) {
        toast.success('Precios actualizados correctamente');
        setShowPricingDialog(false);
        fetchPlans();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al actualizar precios');
      }
    } catch (error) {
      console.error('Error updating pricing:', error);
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePlan = async () => {
    if (!selectedPlan) return;

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/plans/${selectedPlan.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Plan archivado correctamente');
        setShowDeleteDialog(false);
        setSelectedPlan(null);
        fetchPlans();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al eliminar plan');
      }
    } catch (error) {
      console.error('Error deleting plan:', error);
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (plan, newStatus) => {
    const endpoint = newStatus === 'activo' ? 'activate' : 'deactivate';
    try {
      const response = await fetch(`${API_URL}/api/plans/${plan.id}/${endpoint}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success(`Plan ${newStatus === 'activo' ? 'activado' : 'desactivado'}`);
        fetchPlans();
      } else {
        toast.error('Error al cambiar estado');
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Error de conexión');
    }
  };

  const handleDuplicatePlan = async (plan) => {
    const newNombre = `${plan.nombre} (Copia)`;
    const newCodigo = `${plan.codigo}_copy_${Date.now()}`;

    try {
      const response = await fetch(`${API_URL}/api/plans/${plan.id}/duplicate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          nuevo_nombre: newNombre,
          nuevo_codigo: newCodigo
        })
      });

      if (response.ok) {
        toast.success('Plan duplicado correctamente');
        fetchPlans();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al duplicar plan');
      }
    } catch (error) {
      console.error('Error duplicating plan:', error);
      toast.error('Error de conexión');
    }
  };

  const resetForm = () => {
    setPlanForm({
      nombre: '',
      codigo: '',
      descripcion_breve: '',
      descripcion_comercial: '',
      publico_objetivo: '',
      color: '#3B82F6',
      orden: 0,
      etiqueta_destacada: '',
      texto_boton: 'Comenzar',
      es_recomendado: false,
      requiere_cotizacion: false,
      contratacion_directa: true,
      features: []
    });
    setPricingForm({
      precio_mensual: 0,
      precio_anual: 0,
      moneda: 'CLP',
      descuento_anual_porcentaje: 20,
      periodo_prueba_dias: 14,
      cobro_usuario_adicional: 0,
      cobro_establecimiento_adicional: 0
    });
    setSelectedPlan(null);
    setEditMode(false);
  };

  const openEditDialog = (plan) => {
    setSelectedPlan(plan);
    setPlanForm({
      nombre: plan.nombre || '',
      codigo: plan.codigo || '',
      descripcion_breve: plan.descripcion_breve || '',
      descripcion_comercial: plan.descripcion_comercial || '',
      publico_objetivo: plan.publico_objetivo || '',
      color: plan.color || '#3B82F6',
      orden: plan.orden || 0,
      etiqueta_destacada: plan.etiqueta_destacada || '',
      texto_boton: plan.texto_boton || 'Comenzar',
      es_recomendado: plan.es_recomendado || false,
      requiere_cotizacion: plan.requiere_cotizacion || false,
      contratacion_directa: plan.contratacion_directa !== false,
      features: plan.features || []
    });
    setEditMode(true);
    setShowPlanDialog(true);
  };

  const openPricingDialog = (plan) => {
    setSelectedPlan(plan);
    const pricing = plan.pricing || {};
    setPricingForm({
      precio_mensual: pricing.precio_mensual || 0,
      precio_anual: pricing.precio_anual || 0,
      moneda: pricing.moneda || 'CLP',
      descuento_anual_porcentaje: pricing.descuento_anual_porcentaje || 20,
      periodo_prueba_dias: pricing.periodo_prueba_dias || 14,
      cobro_usuario_adicional: pricing.cobro_usuario_adicional || 0,
      cobro_establecimiento_adicional: pricing.cobro_establecimiento_adicional || 0
    });
    setShowPricingDialog(true);
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setPlanForm({
        ...planForm,
        features: [...planForm.features, newFeature.trim()]
      });
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    setPlanForm({
      ...planForm,
      features: planForm.features.filter((_, i) => i !== index)
    });
  };

  const formatPrice = (price) => {
    if (!price || price === 0) return 'Gratis';
    return `$${price.toLocaleString('es-CL')}`;
  };

  const getStatusBadge = (status) => {
    const styles = {
      activo: 'bg-green-100 text-green-700',
      inactivo: 'bg-gray-100 text-gray-700',
      archivado: 'bg-red-100 text-red-700'
    };
    return <Badge className={styles[status] || styles.inactivo}>{status}</Badge>;
  };

  return (
    <>
      <div className="space-y-6" data-testid="plans-management-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Package className="w-7 h-7 text-blue-600" />
              Gestión de Planes SaaS
            </h1>
            <p className="text-gray-500 mt-1">
              Administra los planes comerciales de SimuClinic
            </p>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setShowPlanDialog(true);
            }}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="create-plan-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Plan
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Planes Activos</p>
                  <p className="text-2xl font-bold">
                    {plans.filter(p => p.status === 'activo').length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Check className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Planes Públicos</p>
                  <p className="text-2xl font-bold">
                    {plans.filter(p => p.visibilidad === 'publico').length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Eye className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Módulos Disponibles</p>
                  <p className="text-2xl font-bold">{modulesCatalog.length}</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Layers className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Límites Configurables</p>
                  <p className="text-2xl font-bold">{limitsCatalog.length}</p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Settings className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Plans Table */}
        <Card>
          <CardHeader>
            <CardTitle>Planes Comerciales</CardTitle>
            <CardDescription>
              Listado de todos los planes de suscripción
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : plans.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No hay planes configurados</p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => setShowPlanDialog(true)}
                >
                  Crear primer plan
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Plan</TableHead>
                    <TableHead>Precio Mensual</TableHead>
                    <TableHead>Precio Anual</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Instituciones</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {plans.map((plan) => (
                    <TableRow key={plan.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: plan.color || '#3B82F6' }}
                          />
                          <div>
                            <div className="font-medium flex items-center gap-2">
                              {plan.nombre}
                              {plan.es_recomendado && (
                                <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                              )}
                            </div>
                            <div className="text-sm text-gray-500">{plan.codigo}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {plan.requiere_cotizacion ? (
                          <span className="text-gray-500">Cotización</span>
                        ) : (
                          formatPrice(plan.pricing?.precio_mensual)
                        )}
                      </TableCell>
                      <TableCell>
                        {plan.requiere_cotizacion ? (
                          <span className="text-gray-500">-</span>
                        ) : (
                          formatPrice(plan.pricing?.precio_anual)
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(plan.status)}</TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          <Building2 className="w-3 h-3 mr-1" />
                          {plan.instituciones_count || 0}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(plan)}
                            title="Editar"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openPricingDialog(plan)}
                            title="Precios"
                          >
                            <DollarSign className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDuplicatePlan(plan)}
                            title="Duplicar"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleStatus(plan, plan.status === 'activo' ? 'inactivo' : 'activo')}
                            title={plan.status === 'activo' ? 'Desactivar' : 'Activar'}
                          >
                            {plan.status === 'activo' ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => {
                              setSelectedPlan(plan);
                              setShowDeleteDialog(true);
                            }}
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Plan Dialog */}
        <Dialog open={showPlanDialog} onOpenChange={setShowPlanDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editMode ? 'Editar Plan' : 'Nuevo Plan'}
              </DialogTitle>
              <DialogDescription>
                {editMode ? 'Modifica los datos del plan' : 'Configura un nuevo plan comercial'}
              </DialogDescription>
            </DialogHeader>

            <Tabs defaultValue="general" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="general">General</TabsTrigger>
                <TabsTrigger value="features">Características</TabsTrigger>
              </TabsList>

              <TabsContent value="general" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nombre *</Label>
                    <Input
                      value={planForm.nombre}
                      onChange={(e) => setPlanForm({ ...planForm, nombre: e.target.value })}
                      placeholder="Ej: Profesional"
                    />
                  </div>
                  <div>
                    <Label>Código *</Label>
                    <Input
                      value={planForm.codigo}
                      onChange={(e) => setPlanForm({ ...planForm, codigo: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                      placeholder="Ej: profesional"
                      disabled={editMode}
                    />
                  </div>
                </div>

                <div>
                  <Label>Descripción Breve</Label>
                  <Input
                    value={planForm.descripcion_breve}
                    onChange={(e) => setPlanForm({ ...planForm, descripcion_breve: e.target.value })}
                    placeholder="Descripción corta para cards"
                  />
                </div>

                <div>
                  <Label>Descripción Comercial</Label>
                  <Textarea
                    value={planForm.descripcion_comercial}
                    onChange={(e) => setPlanForm({ ...planForm, descripcion_comercial: e.target.value })}
                    placeholder="Descripción detallada"
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Color</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="color"
                        value={planForm.color}
                        onChange={(e) => setPlanForm({ ...planForm, color: e.target.value })}
                        className="w-16 h-10 p-1"
                      />
                      <Input
                        value={planForm.color}
                        onChange={(e) => setPlanForm({ ...planForm, color: e.target.value })}
                        placeholder="#3B82F6"
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Orden de Visualización</Label>
                    <Input
                      type="number"
                      value={planForm.orden}
                      onChange={(e) => setPlanForm({ ...planForm, orden: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Etiqueta Destacada</Label>
                    <Input
                      value={planForm.etiqueta_destacada}
                      onChange={(e) => setPlanForm({ ...planForm, etiqueta_destacada: e.target.value })}
                      placeholder="Ej: Más Popular"
                    />
                  </div>
                  <div>
                    <Label>Texto del Botón</Label>
                    <Input
                      value={planForm.texto_boton}
                      onChange={(e) => setPlanForm({ ...planForm, texto_boton: e.target.value })}
                      placeholder="Comenzar"
                    />
                  </div>
                </div>

                <div className="flex flex-wrap gap-6">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={planForm.es_recomendado}
                      onCheckedChange={(checked) => setPlanForm({ ...planForm, es_recomendado: checked })}
                    />
                    <Label>Plan Recomendado</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={planForm.requiere_cotizacion}
                      onCheckedChange={(checked) => setPlanForm({ ...planForm, requiere_cotizacion: checked })}
                    />
                    <Label>Requiere Cotización</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={planForm.contratacion_directa}
                      onCheckedChange={(checked) => setPlanForm({ ...planForm, contratacion_directa: checked })}
                    />
                    <Label>Contratación Directa</Label>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="features" className="space-y-4">
                <div>
                  <Label>Agregar Característica</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newFeature}
                      onChange={(e) => setNewFeature(e.target.value)}
                      placeholder="Ej: Hasta 50 usuarios"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                    />
                    <Button type="button" onClick={addFeature}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Características ({planForm.features.length})</Label>
                  {planForm.features.length === 0 ? (
                    <p className="text-sm text-gray-500 py-4 text-center">
                      No hay características agregadas
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {planForm.features.map((feature, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-green-500" />
                            <span>{feature}</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFeature(index)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPlanDialog(false)}>
                Cancelar
              </Button>
              <Button
                onClick={editMode ? handleUpdatePlan : handleCreatePlan}
                disabled={saving}
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editMode ? 'Guardar Cambios' : 'Crear Plan'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Pricing Dialog */}
        <Dialog open={showPricingDialog} onOpenChange={setShowPricingDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Configurar Precios</DialogTitle>
              <DialogDescription>
                Ajusta los precios del plan {selectedPlan?.nombre}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Precio Mensual (CLP)</Label>
                  <Input
                    type="number"
                    value={pricingForm.precio_mensual}
                    onChange={(e) => setPricingForm({ ...pricingForm, precio_mensual: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label>Precio Anual (CLP)</Label>
                  <Input
                    type="number"
                    value={pricingForm.precio_anual}
                    onChange={(e) => setPricingForm({ ...pricingForm, precio_anual: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Descuento Anual (%)</Label>
                  <Input
                    type="number"
                    value={pricingForm.descuento_anual_porcentaje}
                    onChange={(e) => setPricingForm({ ...pricingForm, descuento_anual_porcentaje: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label>Días de Prueba</Label>
                  <Input
                    type="number"
                    value={pricingForm.periodo_prueba_dias}
                    onChange={(e) => setPricingForm({ ...pricingForm, periodo_prueba_dias: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Cobro por Usuario Adicional</Label>
                  <Input
                    type="number"
                    value={pricingForm.cobro_usuario_adicional}
                    onChange={(e) => setPricingForm({ ...pricingForm, cobro_usuario_adicional: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label>Cobro por Establecimiento Adicional</Label>
                  <Input
                    type="number"
                    value={pricingForm.cobro_establecimiento_adicional}
                    onChange={(e) => setPricingForm({ ...pricingForm, cobro_establecimiento_adicional: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPricingDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleUpdatePricing} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Guardar Precios
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertCircle className="w-5 h-5" />
                Archivar Plan
              </DialogTitle>
              <DialogDescription>
                ¿Estás seguro de que deseas archivar el plan &quot;{selectedPlan?.nombre}&quot;?
                Esta acción no eliminará el plan pero lo desactivará.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeletePlan}
                disabled={saving}
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Archivar Plan
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};

export default PlansManagementPage;
